/**
 * Section — universal wrapper for a vertical block of content.
 *
 * The default building unit of a page. Composes a `<SectionHeader>`
 * (when `heading` / `actions` / `badges` are supplied) above its children
 * Stack. Vertical rhythm between sibling Sections is owned by the
 * enclosing `<Stack>` — Section itself doesn't add outer margins.
 *
 * Anatomy:
 *
 *   ┌─────────────────────────────────────────────┐
 *   │ SectionHeader   (optional)                  │
 *   ├─────────────────────────────────────────────┤
 *   │ children                                     │
 *   └─────────────────────────────────────────────┘
 *
 * Pair `<Section heading="..." actions={...}>` to get the standard
 * "uppercase heading + right-action" pattern observed on every Adsensor
 * page (KPI section, Кампании, Все сенсоры, etc.).
 *
 * @format
 */

import {forwardRef, type ElementType, type ReactNode} from 'react';

import {styled} from '@mui/material/styles';

import {Stack} from 'ui/components/layout/Stack';
import type {SpacingTokenName} from 'ui/components/layout/spacing';

import {SectionHeader} from './SectionHeader';

export interface SectionProps {
  /** Section heading text. If omitted, no header is rendered. */
  heading?: ReactNode;
  /** Right-side badges next to the heading (e.g. "Бета", "Не запущен"). */
  badges?: ReactNode;
  /** Right-side actions row next to the heading (CTA, link, etc.). */
  actions?: ReactNode;
  /** Inner gap between children. Default `'m'`. */
  gap?: SpacingTokenName;
  /** Semantic element. Default `'section'`. */
  as?: ElementType;
  className?: string;
  children?: ReactNode;
}

const Root = styled(Stack)({});

export const Section = forwardRef<HTMLDivElement, SectionProps>(function Section(
  {heading, badges, actions, gap = 'm', as = 'section', className, children},
  ref,
) {
  const hasHeader = heading != null || badges != null || actions != null;
  return (
    <Root ref={ref} as={as} gap={gap} className={className}>
      {hasHeader && <SectionHeader title={heading} badges={badges} actions={actions} />}
      {children}
    </Root>
  );
});
